import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  Layers,
  Users,
  Activity,
  Inbox,
  Plus,
  ToggleLeft,
  ToggleRight,
  CheckSquare,
  Square,
  Code2,
  Search,
  XCircle,
  Loader2,
  Trash2,
  Settings,
} from 'lucide-react';
import { GlassCard } from '@/components/glass';
import {
  PageHeader,
  CreateButton,
  BulkActionBar,
} from '@/components/ui';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
} from '@/features/admin/utils/adminAnimations';
import {
  ColorControl,
  SliderControl,
  TextControl,
  ToggleControl,
  SelectControl,
  SectionHeader,
  PreviewBox,
  ControlsPanel,
  ResetButton,
} from './design-system/DesignControls';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

// ---------------------------------------------------------------------------
// Font weight options (shared)
// ---------------------------------------------------------------------------

const FONT_WEIGHT_OPTIONS = [
  { label: '400', value: '400' },
  { label: '500', value: '500' },
  { label: '600', value: '600' },
  { label: '700', value: '700' },
];

const TEXT_TRANSFORM_OPTIONS = [
  { label: 'uppercase', value: 'uppercase' },
  { label: 'none', value: 'none' },
  { label: 'capitalize', value: 'capitalize' },
];

// ---------------------------------------------------------------------------
// Selection demo items
// ---------------------------------------------------------------------------

const SELECTION_ITEMS = [
  { id: '1', name: 'Alpha' },
  { id: '2', name: 'Beta' },
  { id: '3', name: 'Gamma' },
  { id: '4', name: 'Delta' },
  { id: '5', name: 'Epsilon' },
];

// ---------------------------------------------------------------------------
// Helper: hex to rgba
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DesignComponentsPage() {
  const { t } = useTranslation();

  // ===================== 1. GlassCard =====================
  const [gcBgColor, setGcBgColor] = useState('#ffffff');
  const [gcBorderColor, setGcBorderColor] = useState('#94a3b8');
  const [gcBorderWidth, setGcBorderWidth] = useState(1);
  const [gcBorderRadius, setGcBorderRadius] = useState(24);
  const [gcShadowX, setGcShadowX] = useState(0);
  const [gcShadowY, setGcShadowY] = useState(4);
  const [gcShadowBlur, setGcShadowBlur] = useState(16);
  const [gcShadowColor, setGcShadowColor] = useState('#3b82f6');
  const [gcShowHeader, setGcShowHeader] = useState(true);
  const [gcHeaderBg, setGcHeaderBg] = useState('#ffffff');
  const [gcHeaderTextColor, setGcHeaderTextColor] = useState('#64748b');
  const [gcHeaderFontSize, setGcHeaderFontSize] = useState(13);
  const [gcHeaderFontWeight, setGcHeaderFontWeight] = useState('600');
  const [gcHeaderPaddingY, setGcHeaderPaddingY] = useState(10);
  const [gcHeaderBorderShow, setGcHeaderBorderShow] = useState(true);
  const [gcHeaderBorderColor, setGcHeaderBorderColor] = useState('#ffffff');
  const [gcShowFooter, setGcShowFooter] = useState(false);
  const [gcFooterBg, setGcFooterBg] = useState('#f8fafc');
  const [gcPadding, setGcPadding] = useState<'default' | 'compact' | 'none'>('default');
  const [gcCustomPadding, setGcCustomPadding] = useState(20);
  const [gcTitle, setGcTitle] = useState('Card Title');
  const [gcShowIcon, setGcShowIcon] = useState(true);
  const [gcShowHeaderAction, setGcShowHeaderAction] = useState(false);

  const resetGlassCard = () => {
    setGcBgColor('#ffffff'); setGcBorderColor('#94a3b8'); setGcBorderWidth(1);
    setGcBorderRadius(24); setGcShadowX(0); setGcShadowY(4); setGcShadowBlur(16);
    setGcShadowColor('#3b82f6'); setGcShowHeader(true); setGcHeaderBg('#ffffff');
    setGcHeaderTextColor('#64748b'); setGcHeaderFontSize(13); setGcHeaderFontWeight('600');
    setGcHeaderPaddingY(10); setGcHeaderBorderShow(true); setGcHeaderBorderColor('#ffffff');
    setGcShowFooter(false); setGcFooterBg('#f8fafc'); setGcPadding('default');
    setGcCustomPadding(20); setGcTitle('Card Title'); setGcShowIcon(true);
    setGcShowHeaderAction(false);
  };

  // ===================== 2. PageHeader =====================
  const [phIconBg, setPhIconBg] = useState('#ffffff');
  const [phIconBorderColor, setPhIconBorderColor] = useState('#94a3b8');
  const [phIconBorderRadius, setPhIconBorderRadius] = useState(12);
  const [phIconShadow, setPhIconShadow] = useState(true);
  const [phIconSize, setPhIconSize] = useState(56);
  const [phTitleColor, setPhTitleColor] = useState('#1e293b');
  const [phTitleFontSize, setPhTitleFontSize] = useState(24);
  const [phTitleFontWeight, setPhTitleFontWeight] = useState('600');
  const [phSubtitleColor, setPhSubtitleColor] = useState('#64748b');
  const [phSubtitleFontSize, setPhSubtitleFontSize] = useState(14);
  const [phShowSubtitle, setPhShowSubtitle] = useState(true);
  const [phShowAction, setPhShowAction] = useState(false);
  const [phGap, setPhGap] = useState(16);
  const [phTitle, setPhTitle] = useState('Dashboard');
  const [phSubtitle, setPhSubtitle] = useState('Overview of your workspace');

  const resetPageHeader = () => {
    setPhIconBg('#ffffff'); setPhIconBorderColor('#94a3b8'); setPhIconBorderRadius(12);
    setPhIconShadow(true); setPhIconSize(56); setPhTitleColor('#1e293b');
    setPhTitleFontSize(24); setPhTitleFontWeight('600'); setPhSubtitleColor('#64748b');
    setPhSubtitleFontSize(14); setPhShowSubtitle(true); setPhShowAction(false);
    setPhGap(16); setPhTitle('Dashboard'); setPhSubtitle('Overview of your workspace');
  };

  // ===================== 3. StatCard =====================
  const [scBgColor, setScBgColor] = useState('#ffffff');
  const [scBorderColor, setScBorderColor] = useState('#94a3b8');
  const [scBorderRadius, setScBorderRadius] = useState(12);
  const [scShadow, setScShadow] = useState(true);
  const [scIconColor, setScIconColor] = useState('#1e293b');
  const [scIconSize, setScIconSize] = useState(28);
  const [scLabelColor, setScLabelColor] = useState('#64748b');
  const [scLabelFontSize, setScLabelFontSize] = useState(12);
  const [scLabelFontWeight, setScLabelFontWeight] = useState('700');
  const [scLabelTransform, setScLabelTransform] = useState('uppercase');
  const [scValueColor, setScValueColor] = useState('#1e293b');
  const [scValueFontSize, setScValueFontSize] = useState(24);
  const [scValueFontWeight, setScValueFontWeight] = useState('700');
  const [scPadding, setScPadding] = useState(16);
  const [scValue, setScValue] = useState(128);
  const [scLabel, setScLabel] = useState('Therapists');

  const resetStatCard = () => {
    setScBgColor('#ffffff'); setScBorderColor('#94a3b8'); setScBorderRadius(12);
    setScShadow(true); setScIconColor('#1e293b'); setScIconSize(28);
    setScLabelColor('#64748b'); setScLabelFontSize(12); setScLabelFontWeight('700');
    setScLabelTransform('uppercase'); setScValueColor('#1e293b'); setScValueFontSize(24);
    setScValueFontWeight('700'); setScPadding(16); setScValue(128); setScLabel('Therapists');
  };

  // ===================== 4. CardSkeleton =====================
  const [csBgColor, setCsBgColor] = useState('#ffffff');
  const [csBorderColor, setCsBorderColor] = useState('#94a3b8');
  const [csBorderRadius, setCsBorderRadius] = useState(12);
  const [csOpacity, setCsOpacity] = useState(0.6);
  const [csAvatarSize, setCsAvatarSize] = useState(40);
  const [csLineWidth, setCsLineWidth] = useState(60);

  const resetCardSkeleton = () => {
    setCsBgColor('#ffffff'); setCsBorderColor('#94a3b8'); setCsBorderRadius(12);
    setCsOpacity(0.6); setCsAvatarSize(40); setCsLineWidth(60);
  };

  // ===================== 5. EmptyState =====================
  const [esBorderStyle, setEsBorderStyle] = useState('dashed');
  const [esBorderColor, setEsBorderColor] = useState('#94a3b8');
  const [esBgColor, setEsBgColor] = useState('#ffffff');
  const [esIconColor, setEsIconColor] = useState('#64748b');
  const [esIconSize, setEsIconSize] = useState(56);
  const [esMsgColor, setEsMsgColor] = useState('#1e293b');
  const [esMsgFontSize, setEsMsgFontSize] = useState(20);
  const [esMsgFontWeight, setEsMsgFontWeight] = useState('700');
  const [esPadding, setEsPadding] = useState(48);
  const [esMessage, setEsMessage] = useState('No results found');

  const resetEmptyState = () => {
    setEsBorderStyle('dashed'); setEsBorderColor('#94a3b8'); setEsBgColor('#ffffff');
    setEsIconColor('#64748b'); setEsIconSize(56); setEsMsgColor('#1e293b');
    setEsMsgFontSize(20); setEsMsgFontWeight('700'); setEsPadding(48);
    setEsMessage('No results found');
  };

  // ===================== 6. ErrorState =====================
  const [errBgColor, setErrBgColor] = useState('#ffffff');
  const [errBorderColor, setErrBorderColor] = useState('#94a3b8');
  const [errShadow, setErrShadow] = useState(true);
  const [errIconColor, setErrIconColor] = useState('#ef4444');
  const [errIconSize, setErrIconSize] = useState(56);
  const [errMsgColor, setErrMsgColor] = useState('#1e293b');
  const [errMsgFontSize, setErrMsgFontSize] = useState(20);
  const [errMsgFontWeight, setErrMsgFontWeight] = useState('700');
  const [errMessage, setErrMessage] = useState('Something went wrong');

  const resetErrorState = () => {
    setErrBgColor('#ffffff'); setErrBorderColor('#94a3b8'); setErrShadow(true);
    setErrIconColor('#ef4444'); setErrIconSize(56); setErrMsgColor('#1e293b');
    setErrMsgFontSize(20); setErrMsgFontWeight('700'); setErrMessage('Something went wrong');
  };

  // ===================== 7. SearchInput =====================
  const [siBgColor, setSiBgColor] = useState('#ffffff');
  const [siBorderColor, setSiBorderColor] = useState('#94a3b8');
  const [siBorderRadius, setSiBorderRadius] = useState(8);
  const [siShadow, setSiShadow] = useState(true);
  const [siIconColor, setSiIconColor] = useState('#64748b');
  const [siIconSize, setSiIconSize] = useState(20);
  const [siTextColor, setSiTextColor] = useState('#1e293b');
  const [siFontSize, setSiFontSize] = useState(14);
  const [siPlaceholder, setSiPlaceholder] = useState('Search...');
  const [siFocusRingColor, setSiFocusRingColor] = useState('#3b82f6');
  const [siPaddingY, setSiPaddingY] = useState(8);
  const [siPaddingLeft, setSiPaddingLeft] = useState(40);
  const [siValue, setSiValue] = useState('');

  const resetSearchInput = () => {
    setSiBgColor('#ffffff'); setSiBorderColor('#94a3b8'); setSiBorderRadius(8);
    setSiShadow(true); setSiIconColor('#64748b'); setSiIconSize(20);
    setSiTextColor('#1e293b'); setSiFontSize(14); setSiPlaceholder('Search...');
    setSiFocusRingColor('#3b82f6'); setSiPaddingY(8); setSiPaddingLeft(40);
    setSiValue('');
  };

  // ===================== 8. GlassSelect =====================
  const [gsBgColor, setGsBgColor] = useState('#ffffff');
  const [gsBorderColor, setGsBorderColor] = useState('#ffffff');
  const [gsBorderRadius, setGsBorderRadius] = useState(8);
  const [gsTextColor, setGsTextColor] = useState('#1e293b');
  const [gsFontSize, setGsFontSize] = useState(14);
  const [gsFontWeight, setGsFontWeight] = useState('700');
  const [gsPadding, setGsPadding] = useState(8);
  const [gsFocusRingColor, setGsFocusRingColor] = useState('#3b82f6');
  const [gsValue, setGsValue] = useState('a');

  const resetGlassSelect = () => {
    setGsBgColor('#ffffff'); setGsBorderColor('#ffffff'); setGsBorderRadius(8);
    setGsTextColor('#1e293b'); setGsFontSize(14); setGsFontWeight('700');
    setGsPadding(8); setGsFocusRingColor('#3b82f6'); setGsValue('a');
  };

  // ===================== 9. CreateButton =====================
  const [cbBgColor, setCbBgColor] = useState('#ffffff');
  const [cbBorderColor, setCbBorderColor] = useState('#94a3b8');
  const [cbBorderRadius, setCbBorderRadius] = useState(12);
  const [cbShadow, setCbShadow] = useState(true);
  const [cbTextColor, setCbTextColor] = useState('#1e293b');
  const [cbFontSize, setCbFontSize] = useState(14);
  const [cbFontWeight, setCbFontWeight] = useState('700');
  const [cbShowIcon, setCbShowIcon] = useState(true);
  const [cbIconColor, setCbIconColor] = useState('#1e293b');
  const [cbPadding, setCbPadding] = useState(16);
  const [cbLabel, setCbLabel] = useState('New Item');

  const resetCreateButton = () => {
    setCbBgColor('#ffffff'); setCbBorderColor('#94a3b8'); setCbBorderRadius(12);
    setCbShadow(true); setCbTextColor('#1e293b'); setCbFontSize(14);
    setCbFontWeight('700'); setCbShowIcon(true); setCbIconColor('#1e293b');
    setCbPadding(16); setCbLabel('New Item');
  };

  // ===================== 10. BulkActionBar =====================
  const [bulkCount, setBulkCount] = useState(3);
  const [bulkBadgeBg, setBulkBadgeBg] = useState('#ffffff');
  const [bulkBadgeText, setBulkBadgeText] = useState('#1e293b');
  const [bulkBadgeSize, setBulkBadgeSize] = useState(40);
  const [bulkMushroomStart, setBulkMushroomStart] = useState('#ff8c8c');
  const [bulkMushroomEnd, setBulkMushroomEnd] = useState('#c83232');
  const [bulkMushroomSize, setBulkMushroomSize] = useState(62);
  const [bulkDismissBg, setBulkDismissBg] = useState('#ffffff');
  const [bulkDismissSize, setBulkDismissSize] = useState(40);

  const resetBulkActionBar = () => {
    setBulkCount(3); setBulkBadgeBg('#ffffff'); setBulkBadgeText('#1e293b');
    setBulkBadgeSize(40); setBulkMushroomStart('#ff8c8c'); setBulkMushroomEnd('#c83232');
    setBulkMushroomSize(62); setBulkDismissBg('#ffffff'); setBulkDismissSize(40);
  };

  // ===================== 11. StatusDot =====================
  const [sdSize, setSdSize] = useState(10);
  const [sdActiveColor, setSdActiveColor] = useState('#047857');
  const [sdInactiveColor, setSdInactiveColor] = useState('#9ca3af');
  const [sdActive, setSdActive] = useState(true);

  const resetStatusDot = () => {
    setSdSize(10); setSdActiveColor('#047857'); setSdInactiveColor('#9ca3af'); setSdActive(true);
  };

  // ===================== 12. InfiniteScrollSentinel =====================
  const [isSpinnerColor, setIsSpinnerColor] = useState('#1e293b');
  const [isSpinnerSize, setIsSpinnerSize] = useState(20);
  const [isHasNextPage, setIsHasNextPage] = useState(true);
  const [isIsFetching, setIsIsFetching] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const resetInfiniteScroll = () => {
    setIsSpinnerColor('#1e293b'); setIsSpinnerSize(20);
    setIsHasNextPage(true); setIsIsFetching(true);
  };

  // ===================== Hooks =====================
  const isRTL = useRTL();
  const selection = useSelectionSet(SELECTION_ITEMS);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Page header */}
      <PageHeader
        icon={<Layers size={28} className="text-slate-700" />}
        title={t('designSystem.componentsTitle')}
        subtitle={t('designSystem.componentsSubtitle')}
      />

      {/* ================================================================= */}
      {/* 1. GlassCard                                                      */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="GlassCard">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Card Container</SectionHeader>
                <ResetButton onClick={resetGlassCard} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={gcBgColor} onChange={setGcBgColor} />
                <ColorControl label="Border" value={gcBorderColor} onChange={setGcBorderColor} />
                <SliderControl label="Border W" value={gcBorderWidth} onChange={setGcBorderWidth} min={0} max={4} unit="px" />
                <SliderControl label="Radius" value={gcBorderRadius} onChange={setGcBorderRadius} min={0} max={32} unit="px" />
              </ControlsPanel>

              <SectionHeader>Shadow</SectionHeader>
              <ControlsPanel>
                <SliderControl label="X" value={gcShadowX} onChange={setGcShadowX} min={-20} max={20} unit="px" />
                <SliderControl label="Y" value={gcShadowY} onChange={setGcShadowY} min={-20} max={20} unit="px" />
                <SliderControl label="Blur" value={gcShadowBlur} onChange={setGcShadowBlur} min={0} max={40} unit="px" />
                <ColorControl label="Color" value={gcShadowColor} onChange={setGcShadowColor} />
              </ControlsPanel>

              <SectionHeader>Header</SectionHeader>
              <ControlsPanel>
                <ToggleControl label="Show header" value={gcShowHeader} onChange={setGcShowHeader} />
                <ColorControl label="Header bg" value={gcHeaderBg} onChange={setGcHeaderBg} />
                <ColorControl label="Text color" value={gcHeaderTextColor} onChange={setGcHeaderTextColor} />
                <SliderControl label="Font size" value={gcHeaderFontSize} onChange={setGcHeaderFontSize} min={10} max={18} unit="px" />
                <SelectControl label="Weight" value={gcHeaderFontWeight} onChange={setGcHeaderFontWeight} options={FONT_WEIGHT_OPTIONS} />
                <SliderControl label="Pad Y" value={gcHeaderPaddingY} onChange={setGcHeaderPaddingY} min={4} max={20} unit="px" />
                <ToggleControl label="Border below" value={gcHeaderBorderShow} onChange={setGcHeaderBorderShow} />
                <ColorControl label="Border clr" value={gcHeaderBorderColor} onChange={setGcHeaderBorderColor} />
              </ControlsPanel>

              <SectionHeader>Footer</SectionHeader>
              <ControlsPanel>
                <ToggleControl label="Show footer" value={gcShowFooter} onChange={setGcShowFooter} />
                <ColorControl label="Footer bg" value={gcFooterBg} onChange={setGcFooterBg} />
              </ControlsPanel>

              <SectionHeader>Content</SectionHeader>
              <ControlsPanel>
                <SelectControl
                  label="Padding"
                  value={gcPadding}
                  onChange={(v) => setGcPadding(v as 'default' | 'compact' | 'none')}
                  options={[
                    { label: 'default', value: 'default' },
                    { label: 'compact', value: 'compact' },
                    { label: 'none', value: 'none' },
                  ]}
                />
                <SliderControl label="Custom pad" value={gcCustomPadding} onChange={setGcCustomPadding} min={0} max={40} unit="px" />
                <TextControl label="Title" value={gcTitle} onChange={setGcTitle} />
                <ToggleControl label="Show icon" value={gcShowIcon} onChange={setGcShowIcon} />
                <ToggleControl label="Header action" value={gcShowHeaderAction} onChange={setGcShowHeaderAction} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div
                className="overflow-hidden"
                style={{
                  backgroundColor: hexToRgba(gcBgColor, 0.73),
                  border: `${gcBorderWidth}px solid ${hexToRgba(gcBorderColor, 0.3)}`,
                  borderRadius: `${gcBorderRadius}px`,
                  boxShadow: `${gcShadowX}px ${gcShadowY}px ${gcShadowBlur}px ${hexToRgba(gcShadowColor, 0.1)}`,
                }}
              >
                {gcShowHeader && (
                  <div
                    className="px-5 flex items-center justify-between"
                    style={{
                      backgroundColor: hexToRgba(gcHeaderBg, 0.3),
                      paddingTop: `${gcHeaderPaddingY}px`,
                      paddingBottom: `${gcHeaderPaddingY}px`,
                      borderBottom: gcHeaderBorderShow
                        ? `1px solid ${hexToRgba(gcHeaderBorderColor, 0.3)}`
                        : 'none',
                    }}
                  >
                    <h2
                      style={{
                        color: gcHeaderTextColor,
                        fontSize: `${gcHeaderFontSize}px`,
                        fontWeight: Number(gcHeaderFontWeight),
                      }}
                    >
                      {gcTitle}
                    </h2>
                    <div className="flex items-center gap-2">
                      {gcShowHeaderAction && (
                        <button className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600 font-medium">
                          Action
                        </button>
                      )}
                      {gcShowIcon && (
                        <Settings className="w-4 h-4" style={{ color: gcHeaderTextColor }} />
                      )}
                    </div>
                  </div>
                )}
                <div style={{ padding: gcPadding === 'none' ? 0 : `${gcCustomPadding}px` }}>
                  <p className="text-sm text-slate-600">
                    Card content goes here. This area respects the padding controls above.
                  </p>
                  <div className="mt-3 h-8 rounded bg-slate-100/50" />
                </div>
                {gcShowFooter && (
                  <div
                    className="px-5 py-3 flex items-center justify-end"
                    style={{
                      backgroundColor: hexToRgba(gcFooterBg, 0.5),
                      borderTop: `1px solid ${hexToRgba(gcBorderColor, 0.2)}`,
                    }}
                  >
                    <span className="text-xs text-slate-500">Footer area</span>
                  </div>
                )}
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 2. PageHeader                                                     */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="PageHeader">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Icon Container</SectionHeader>
                <ResetButton onClick={resetPageHeader} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={phIconBg} onChange={setPhIconBg} />
                <ColorControl label="Border" value={phIconBorderColor} onChange={setPhIconBorderColor} />
                <SliderControl label="Radius" value={phIconBorderRadius} onChange={setPhIconBorderRadius} min={4} max={24} unit="px" />
                <ToggleControl label="Shadow" value={phIconShadow} onChange={setPhIconShadow} />
                <SliderControl label="Size" value={phIconSize} onChange={setPhIconSize} min={32} max={64} unit="px" />
              </ControlsPanel>

              <SectionHeader>Title</SectionHeader>
              <ControlsPanel>
                <TextControl label="Text" value={phTitle} onChange={setPhTitle} />
                <ColorControl label="Color" value={phTitleColor} onChange={setPhTitleColor} />
                <SliderControl label="Font size" value={phTitleFontSize} onChange={setPhTitleFontSize} min={14} max={32} unit="px" />
                <SelectControl label="Weight" value={phTitleFontWeight} onChange={setPhTitleFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>

              <SectionHeader>Subtitle</SectionHeader>
              <ControlsPanel>
                <ToggleControl label="Show" value={phShowSubtitle} onChange={setPhShowSubtitle} />
                <TextControl label="Text" value={phSubtitle} onChange={setPhSubtitle} />
                <ColorControl label="Color" value={phSubtitleColor} onChange={setPhSubtitleColor} />
                <SliderControl label="Font size" value={phSubtitleFontSize} onChange={setPhSubtitleFontSize} min={10} max={18} unit="px" />
              </ControlsPanel>

              <SectionHeader>Layout</SectionHeader>
              <ControlsPanel>
                <ToggleControl label="Show action" value={phShowAction} onChange={setPhShowAction} />
                <SliderControl label="Gap" value={phGap} onChange={setPhGap} min={8} max={24} unit="px" />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div className="flex items-center justify-between">
                <div className="flex items-center" style={{ gap: `${phGap}px` }}>
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: `${phIconSize}px`,
                      height: `${phIconSize}px`,
                      borderRadius: `${phIconBorderRadius}px`,
                      backgroundColor: hexToRgba(phIconBg, 0.45),
                      border: `1px solid ${hexToRgba(phIconBorderColor, 0.3)}`,
                      boxShadow: phIconShadow ? ADMIN_COLORS.shadow : 'none',
                    }}
                  >
                    <Activity size={Math.round(phIconSize * 0.5)} className="text-slate-700" />
                  </div>
                  <div>
                    <h1
                      style={{
                        color: phTitleColor,
                        fontSize: `${phTitleFontSize}px`,
                        fontWeight: Number(phTitleFontWeight),
                        letterSpacing: '0.025em',
                      }}
                    >
                      {phTitle}
                    </h1>
                    {phShowSubtitle && (
                      <p
                        style={{
                          color: phSubtitleColor,
                          fontSize: `${phSubtitleFontSize}px`,
                        }}
                      >
                        {phSubtitle}
                      </p>
                    )}
                  </div>
                </div>
                {phShowAction && (
                  <CreateButton label="Action" onClick={() => {}} />
                )}
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 3. StatCard                                                       */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="StatCard">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Card</SectionHeader>
                <ResetButton onClick={resetStatCard} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={scBgColor} onChange={setScBgColor} />
                <ColorControl label="Border" value={scBorderColor} onChange={setScBorderColor} />
                <SliderControl label="Radius" value={scBorderRadius} onChange={setScBorderRadius} min={0} max={24} unit="px" />
                <ToggleControl label="Shadow" value={scShadow} onChange={setScShadow} />
                <SliderControl label="Padding" value={scPadding} onChange={setScPadding} min={8} max={24} unit="px" />
              </ControlsPanel>

              <SectionHeader>Icon</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={scIconColor} onChange={setScIconColor} />
                <SliderControl label="Size" value={scIconSize} onChange={setScIconSize} min={16} max={40} unit="px" />
              </ControlsPanel>

              <SectionHeader>Label</SectionHeader>
              <ControlsPanel>
                <TextControl label="Text" value={scLabel} onChange={setScLabel} />
                <ColorControl label="Color" value={scLabelColor} onChange={setScLabelColor} />
                <SliderControl label="Font size" value={scLabelFontSize} onChange={setScLabelFontSize} min={8} max={14} unit="px" />
                <SelectControl label="Weight" value={scLabelFontWeight} onChange={setScLabelFontWeight} options={FONT_WEIGHT_OPTIONS} />
                <SelectControl label="Transform" value={scLabelTransform} onChange={setScLabelTransform} options={TEXT_TRANSFORM_OPTIONS} />
              </ControlsPanel>

              <SectionHeader>Value</SectionHeader>
              <ControlsPanel>
                <SliderControl label="Number" value={scValue} onChange={setScValue} min={0} max={999} />
                <ColorControl label="Color" value={scValueColor} onChange={setScValueColor} />
                <SliderControl label="Font size" value={scValueFontSize} onChange={setScValueFontSize} min={14} max={36} unit="px" />
                <SelectControl label="Weight" value={scValueFontWeight} onChange={setScValueFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div
                className="flex items-center gap-3"
                style={{
                  padding: `${scPadding}px`,
                  borderRadius: `${scBorderRadius}px`,
                  backgroundColor: hexToRgba(scBgColor, 0.45),
                  border: `1px solid ${hexToRgba(scBorderColor, 0.3)}`,
                  boxShadow: scShadow ? ADMIN_COLORS.shadow : 'none',
                }}
              >
                <div
                  className="flex-shrink-0"
                  style={{
                    width: `${scIconSize}px`,
                    height: `${scIconSize}px`,
                    color: hexToRgba(scIconColor, 0.5),
                  }}
                >
                  <Users className="w-full h-full" />
                </div>
                <div>
                  <p
                    style={{
                      color: scLabelColor,
                      fontSize: `${scLabelFontSize}px`,
                      fontWeight: Number(scLabelFontWeight),
                      textTransform: scLabelTransform as 'uppercase' | 'none' | 'capitalize',
                    }}
                  >
                    {scLabel}
                  </p>
                  <p
                    style={{
                      color: scValueColor,
                      fontSize: `${scValueFontSize}px`,
                      fontWeight: Number(scValueFontWeight),
                    }}
                  >
                    {scValue.toLocaleString()}
                  </p>
                </div>
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 4. CardSkeleton                                                   */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="CardSkeleton">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetCardSkeleton} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={csBgColor} onChange={setCsBgColor} />
                <ColorControl label="Border" value={csBorderColor} onChange={setCsBorderColor} />
                <SliderControl label="Radius" value={csBorderRadius} onChange={setCsBorderRadius} min={0} max={24} unit="px" />
                <SliderControl label="Opacity" value={csOpacity} onChange={setCsOpacity} min={0} max={1} step={0.05} />
              </ControlsPanel>

              <SectionHeader>Content</SectionHeader>
              <ControlsPanel>
                <SliderControl label="Avatar" value={csAvatarSize} onChange={setCsAvatarSize} min={24} max={64} unit="px" />
                <SliderControl label="Line W %" value={csLineWidth} onChange={setCsLineWidth} min={20} max={100} unit="%" />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between w-full py-3 px-3 animate-pulse"
                    style={{
                      backgroundColor: hexToRgba(csBgColor, 0.45),
                      border: `1px solid ${hexToRgba(csBorderColor, 0.3)}`,
                      borderRadius: `${csBorderRadius}px`,
                      boxShadow: ADMIN_COLORS.shadow,
                      opacity: csOpacity,
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className="rounded-full flex-shrink-0"
                        style={{
                          width: `${csAvatarSize}px`,
                          height: `${csAvatarSize}px`,
                          backgroundColor: hexToRgba(csBgColor, 0.45),
                          border: `1px solid ${hexToRgba(csBorderColor, 0.3)}`,
                        }}
                      />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div
                          className="h-4 rounded"
                          style={{
                            width: `${csLineWidth * 0.5}%`,
                            backgroundColor: hexToRgba(csBgColor, 0.45),
                          }}
                        />
                        <div
                          className="h-3 rounded"
                          style={{
                            width: `${csLineWidth}%`,
                            backgroundColor: hexToRgba(csBgColor, 0.45),
                            opacity: 0.6,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 5. EmptyState                                                     */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="EmptyState">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetEmptyState} />
              </div>
              <ControlsPanel>
                <SelectControl
                  label="Border style"
                  value={esBorderStyle}
                  onChange={setEsBorderStyle}
                  options={[
                    { label: 'dashed', value: 'dashed' },
                    { label: 'solid', value: 'solid' },
                    { label: 'dotted', value: 'dotted' },
                  ]}
                />
                <ColorControl label="Border" value={esBorderColor} onChange={setEsBorderColor} />
                <ColorControl label="Bg" value={esBgColor} onChange={setEsBgColor} />
                <SliderControl label="Padding" value={esPadding} onChange={setEsPadding} min={16} max={64} unit="px" />
              </ControlsPanel>

              <SectionHeader>Icon</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={esIconColor} onChange={setEsIconColor} />
                <SliderControl label="Size" value={esIconSize} onChange={setEsIconSize} min={40} max={80} unit="px" />
              </ControlsPanel>

              <SectionHeader>Message</SectionHeader>
              <ControlsPanel>
                <TextControl label="Text" value={esMessage} onChange={setEsMessage} />
                <ColorControl label="Color" value={esMsgColor} onChange={setEsMsgColor} />
                <SliderControl label="Font size" value={esMsgFontSize} onChange={setEsMsgFontSize} min={12} max={28} unit="px" />
                <SelectControl label="Weight" value={esMsgFontWeight} onChange={setEsMsgFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div
                className="text-center rounded-xl"
                style={{
                  border: `2px ${esBorderStyle} ${hexToRgba(esBorderColor, 0.3)}`,
                  backgroundColor: hexToRgba(esBgColor, 0.45),
                  padding: `${esPadding}px`,
                  opacity: 0.8,
                }}
              >
                <div
                  className="mx-auto mb-4"
                  style={{
                    width: `${esIconSize}px`,
                    height: `${esIconSize}px`,
                    color: esIconColor,
                  }}
                >
                  <Inbox className="w-full h-full" />
                </div>
                <h3
                  className="mb-2"
                  style={{
                    color: esMsgColor,
                    fontSize: `${esMsgFontSize}px`,
                    fontWeight: Number(esMsgFontWeight),
                  }}
                >
                  {esMessage}
                </h3>
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 6. ErrorState                                                     */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="ErrorState">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetErrorState} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={errBgColor} onChange={setErrBgColor} />
                <ColorControl label="Border" value={errBorderColor} onChange={setErrBorderColor} />
                <ToggleControl label="Shadow" value={errShadow} onChange={setErrShadow} />
              </ControlsPanel>

              <SectionHeader>Icon</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={errIconColor} onChange={setErrIconColor} />
                <SliderControl label="Size" value={errIconSize} onChange={setErrIconSize} min={32} max={72} unit="px" />
              </ControlsPanel>

              <SectionHeader>Message</SectionHeader>
              <ControlsPanel>
                <TextControl label="Text" value={errMessage} onChange={setErrMessage} />
                <ColorControl label="Color" value={errMsgColor} onChange={setErrMsgColor} />
                <SliderControl label="Font size" value={errMsgFontSize} onChange={setErrMsgFontSize} min={12} max={28} unit="px" />
                <SelectControl label="Weight" value={errMsgFontWeight} onChange={setErrMsgFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div
                className="rounded-xl p-8 text-center"
                style={{
                  backgroundColor: hexToRgba(errBgColor, 0.45),
                  border: `1px solid ${hexToRgba(errBorderColor, 0.3)}`,
                  boxShadow: errShadow ? ADMIN_COLORS.shadow : 'none',
                }}
              >
                <XCircle
                  className="mx-auto mb-4"
                  style={{
                    width: `${errIconSize}px`,
                    height: `${errIconSize}px`,
                    color: errIconColor,
                  }}
                />
                <p
                  style={{
                    color: errMsgColor,
                    fontSize: `${errMsgFontSize}px`,
                    fontWeight: Number(errMsgFontWeight),
                  }}
                >
                  {errMessage}
                </p>
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 7. SearchInput                                                    */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="SearchInput">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetSearchInput} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={siBgColor} onChange={setSiBgColor} />
                <ColorControl label="Border" value={siBorderColor} onChange={setSiBorderColor} />
                <SliderControl label="Radius" value={siBorderRadius} onChange={setSiBorderRadius} min={4} max={20} unit="px" />
                <ToggleControl label="Shadow" value={siShadow} onChange={setSiShadow} />
              </ControlsPanel>

              <SectionHeader>Icon</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={siIconColor} onChange={setSiIconColor} />
                <SliderControl label="Size" value={siIconSize} onChange={setSiIconSize} min={14} max={28} unit="px" />
              </ControlsPanel>

              <SectionHeader>Text</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={siTextColor} onChange={setSiTextColor} />
                <SliderControl label="Font size" value={siFontSize} onChange={setSiFontSize} min={12} max={18} unit="px" />
                <TextControl label="Placeholder" value={siPlaceholder} onChange={setSiPlaceholder} />
                <ColorControl label="Focus ring" value={siFocusRingColor} onChange={setSiFocusRingColor} />
              </ControlsPanel>

              <SectionHeader>Spacing</SectionHeader>
              <ControlsPanel>
                <SliderControl label="Pad Y" value={siPaddingY} onChange={setSiPaddingY} min={4} max={16} unit="px" />
                <SliderControl label="Pad left" value={siPaddingLeft} onChange={setSiPaddingLeft} min={28} max={48} unit="px" />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div className="flex-1 relative">
                <Search
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{
                    left: '12px',
                    width: `${siIconSize}px`,
                    height: `${siIconSize}px`,
                    color: siIconColor,
                  }}
                />
                <input
                  type="text"
                  value={siValue}
                  onChange={(e) => setSiValue(e.target.value)}
                  placeholder={siPlaceholder}
                  className="w-full focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: hexToRgba(siBgColor, 0.45),
                    border: `1px solid ${hexToRgba(siBorderColor, 0.3)}`,
                    borderRadius: `${siBorderRadius}px`,
                    boxShadow: siShadow ? ADMIN_COLORS.shadow : 'none',
                    color: siTextColor,
                    fontSize: `${siFontSize}px`,
                    paddingTop: `${siPaddingY}px`,
                    paddingBottom: `${siPaddingY}px`,
                    paddingLeft: `${siPaddingLeft}px`,
                    paddingRight: '16px',
                    // @ts-expect-error CSS custom property for focus ring
                    '--tw-ring-color': siFocusRingColor,
                  }}
                />
                {siValue && (
                  <p className="mt-2 text-xs text-slate-500">
                    Current value: &ldquo;{siValue}&rdquo;
                  </p>
                )}
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 8. GlassSelect                                                    */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="GlassSelect">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetGlassSelect} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={gsBgColor} onChange={setGsBgColor} />
                <ColorControl label="Border" value={gsBorderColor} onChange={setGsBorderColor} />
                <SliderControl label="Radius" value={gsBorderRadius} onChange={setGsBorderRadius} min={4} max={20} unit="px" />
              </ControlsPanel>

              <SectionHeader>Text</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Color" value={gsTextColor} onChange={setGsTextColor} />
                <SliderControl label="Font size" value={gsFontSize} onChange={setGsFontSize} min={12} max={18} unit="px" />
                <SelectControl label="Weight" value={gsFontWeight} onChange={setGsFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>

              <SectionHeader>Spacing</SectionHeader>
              <ControlsPanel>
                <SliderControl label="Padding" value={gsPadding} onChange={setGsPadding} min={4} max={16} unit="px" />
                <ColorControl label="Focus ring" value={gsFocusRingColor} onChange={setGsFocusRingColor} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <select
                value={gsValue}
                onChange={(e) => setGsValue(e.target.value)}
                className="cursor-pointer focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: hexToRgba(gsBgColor, 0.5),
                  border: `1px solid ${hexToRgba(gsBorderColor, 0.3)}`,
                  borderRadius: `${gsBorderRadius}px`,
                  color: gsTextColor,
                  fontSize: `${gsFontSize}px`,
                  fontWeight: Number(gsFontWeight),
                  padding: `${gsPadding}px ${gsPadding + 8}px`,
                  // @ts-expect-error CSS custom property for focus ring
                  '--tw-ring-color': gsFocusRingColor,
                }}
              >
                <option value="a">Option A</option>
                <option value="b">Option B</option>
                <option value="c">Option C</option>
              </select>
              <p className="mt-2 text-xs text-slate-500">
                Selected: {gsValue.toUpperCase()}
              </p>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 9. CreateButton                                                   */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="CreateButton">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Container</SectionHeader>
                <ResetButton onClick={resetCreateButton} />
              </div>
              <ControlsPanel>
                <ColorControl label="Bg" value={cbBgColor} onChange={setCbBgColor} />
                <ColorControl label="Border" value={cbBorderColor} onChange={setCbBorderColor} />
                <SliderControl label="Radius" value={cbBorderRadius} onChange={setCbBorderRadius} min={4} max={24} unit="px" />
                <ToggleControl label="Shadow" value={cbShadow} onChange={setCbShadow} />
                <SliderControl label="Padding" value={cbPadding} onChange={setCbPadding} min={8} max={24} unit="px" />
              </ControlsPanel>

              <SectionHeader>Text</SectionHeader>
              <ControlsPanel>
                <TextControl label="Label" value={cbLabel} onChange={setCbLabel} />
                <ColorControl label="Color" value={cbTextColor} onChange={setCbTextColor} />
                <SliderControl label="Font size" value={cbFontSize} onChange={setCbFontSize} min={12} max={18} unit="px" />
                <SelectControl label="Weight" value={cbFontWeight} onChange={setCbFontWeight} options={FONT_WEIGHT_OPTIONS} />
              </ControlsPanel>

              <SectionHeader>Icon</SectionHeader>
              <ControlsPanel>
                <ToggleControl label="Show icon" value={cbShowIcon} onChange={setCbShowIcon} />
                <ColorControl label="Icon color" value={cbIconColor} onChange={setCbIconColor} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <button
                onClick={() => alert(`CreateButton clicked: "${cbLabel}"`)}
                className="flex items-center gap-2 cursor-pointer transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
                style={{
                  backgroundColor: hexToRgba(cbBgColor, 0.45),
                  border: `1px solid ${hexToRgba(cbBorderColor, 0.3)}`,
                  borderRadius: `${cbBorderRadius}px`,
                  boxShadow: cbShadow ? ADMIN_COLORS.shadow : 'none',
                  padding: `${cbPadding}px ${cbPadding + 8}px`,
                  color: cbTextColor,
                  fontSize: `${cbFontSize}px`,
                  fontWeight: Number(cbFontWeight),
                }}
              >
                {cbShowIcon && (
                  <Plus className="w-5 h-5" style={{ color: cbIconColor }} />
                )}
                <span>{cbLabel}</span>
              </button>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 10. BulkActionBar + MushroomButton                                */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="BulkActionBar + MushroomButton">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Bar</SectionHeader>
                <ResetButton onClick={resetBulkActionBar} />
              </div>
              <ControlsPanel>
                <SliderControl label="Count" value={bulkCount} onChange={setBulkCount} min={0} max={10} />
              </ControlsPanel>

              <SectionHeader>Badge</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Bg" value={bulkBadgeBg} onChange={setBulkBadgeBg} />
                <ColorControl label="Text" value={bulkBadgeText} onChange={setBulkBadgeText} />
                <SliderControl label="Size" value={bulkBadgeSize} onChange={setBulkBadgeSize} min={24} max={48} unit="px" />
              </ControlsPanel>

              <SectionHeader>Mushroom (Delete)</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Grad start" value={bulkMushroomStart} onChange={setBulkMushroomStart} />
                <ColorControl label="Grad end" value={bulkMushroomEnd} onChange={setBulkMushroomEnd} />
                <SliderControl label="Size" value={bulkMushroomSize} onChange={setBulkMushroomSize} min={40} max={80} unit="px" />
              </ControlsPanel>

              <SectionHeader>Dismiss</SectionHeader>
              <ControlsPanel>
                <ColorControl label="Bg" value={bulkDismissBg} onChange={setBulkDismissBg} />
                <SliderControl label="Size" value={bulkDismissSize} onChange={setBulkDismissSize} min={24} max={48} unit="px" />
              </ControlsPanel>
            </div>

            {/* Preview — static inline replica */}
            <PreviewBox>
              <p className="text-sm text-slate-600 mb-4">
                The real BulkActionBar renders fixed at the viewport bottom when count &gt; 0.
                Below is an inline replica with your controls applied.
              </p>
              <div className="flex items-center gap-3 justify-center">
                {/* Badge */}
                <div
                  className="rounded-full border border-white/30 flex items-center justify-center font-bold text-sm"
                  style={{
                    width: `${bulkBadgeSize}px`,
                    height: `${bulkBadgeSize}px`,
                    backgroundColor: bulkBadgeBg,
                    color: bulkBadgeText,
                    boxShadow: ADMIN_COLORS.shadow,
                  }}
                >
                  {bulkCount}
                </div>

                {/* Mushroom delete */}
                <div
                  className="relative flex items-center justify-center rounded-full overflow-hidden"
                  style={{
                    width: `${bulkMushroomSize}px`,
                    height: `${bulkMushroomSize}px`,
                    background: `radial-gradient(ellipse at 50% 30%, ${bulkMushroomStart}F2 0%, ${hexToRgba(bulkMushroomEnd, 0.85)} 60%, ${hexToRgba(bulkMushroomEnd, 0.8)} 100%)`,
                    border: `1px solid ${ADMIN_COLORS.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <div
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      top: '6px',
                      left: `${bulkMushroomSize * 0.22}px`,
                      width: `${bulkMushroomSize * 0.29}px`,
                      height: `${bulkMushroomSize * 0.16}px`,
                      background: 'rgba(255,255,255,0.45)',
                      borderRadius: '50%',
                    }}
                  />
                  <Trash2
                    className="text-slate-700 relative z-[1]"
                    style={{
                      width: `${bulkMushroomSize * 0.45}px`,
                      height: `${bulkMushroomSize * 0.45}px`,
                    }}
                  />
                </div>

                {/* Dismiss */}
                <div
                  className="rounded-full border border-white/30 flex items-center justify-center cursor-pointer"
                  style={{
                    width: `${bulkDismissSize}px`,
                    height: `${bulkDismissSize}px`,
                    backgroundColor: bulkDismissBg,
                    boxShadow: ADMIN_COLORS.shadow,
                  }}
                >
                  <XCircle className="w-4 h-4" />
                </div>
              </div>
            </PreviewBox>
          </div>

          {/* Actual floating bar (real component) */}
          <BulkActionBar
            count={bulkCount}
            onDelete={() => alert('Delete pressed')}
            onClear={() => setBulkCount(0)}
          />
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 11. StatusDot                                                     */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="StatusDot">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Dot</SectionHeader>
                <ResetButton onClick={resetStatusDot} />
              </div>
              <ControlsPanel>
                <SliderControl label="Size" value={sdSize} onChange={setSdSize} min={4} max={20} unit="px" />
                <ColorControl label="Active" value={sdActiveColor} onChange={setSdActiveColor} />
                <ColorControl label="Inactive" value={sdInactiveColor} onChange={setSdInactiveColor} />
                <ToggleControl label="Active" value={sdActive} onChange={setSdActive} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              <div className="flex items-center gap-3">
                <div
                  className="rounded-full"
                  style={{
                    width: `${sdSize}px`,
                    height: `${sdSize}px`,
                    backgroundColor: sdActive ? sdActiveColor : sdInactiveColor,
                  }}
                />
                <span className="text-sm font-medium">
                  {sdActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              {/* Multiple dots side by side for comparison */}
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <div
                    className="rounded-full"
                    style={{
                      width: `${sdSize}px`,
                      height: `${sdSize}px`,
                      backgroundColor: sdActiveColor,
                    }}
                  />
                  <span className="text-xs text-slate-500">Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="rounded-full"
                    style={{
                      width: `${sdSize}px`,
                      height: `${sdSize}px`,
                      backgroundColor: sdInactiveColor,
                    }}
                  />
                  <span className="text-xs text-slate-500">Inactive</span>
                </div>
              </div>
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* 12. InfiniteScrollSentinel                                        */}
      {/* ================================================================= */}
      <motion.div variants={staggerItem}>
        <GlassCard title="InfiniteScrollSentinel">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader>Spinner</SectionHeader>
                <ResetButton onClick={resetInfiniteScroll} />
              </div>
              <ControlsPanel>
                <ColorControl label="Color" value={isSpinnerColor} onChange={setIsSpinnerColor} />
                <SliderControl label="Size" value={isSpinnerSize} onChange={setIsSpinnerSize} min={12} max={32} unit="px" />
                <ToggleControl label="hasNextPage" value={isHasNextPage} onChange={setIsHasNextPage} />
                <ToggleControl label="isFetchingNextPage" value={isIsFetching} onChange={setIsIsFetching} />
              </ControlsPanel>
            </div>

            {/* Preview */}
            <PreviewBox>
              {isHasNextPage ? (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {isIsFetching && (
                    <Loader2
                      className="animate-spin"
                      style={{
                        width: `${isSpinnerSize}px`,
                        height: `${isSpinnerSize}px`,
                        color: isSpinnerColor,
                      }}
                    />
                  )}
                  {!isIsFetching && (
                    <p className="text-xs text-slate-400">Sentinel visible, waiting for intersection...</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-center text-slate-500">
                  hasNextPage is false -- sentinel is hidden.
                </p>
              )}
            </PreviewBox>
          </div>
        </GlassCard>
      </motion.div>

      {/* ================================================================= */}
      {/* HOOKS                                                             */}
      {/* ================================================================= */}

      {/* ----------------------------------------------------------------- */}
      {/* useRTL                                                            */}
      {/* ----------------------------------------------------------------- */}
      <motion.div variants={staggerItem}>
        <GlassCard title="useRTL">
          <PreviewBox>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                Current direction:{' '}
                <code className="px-1.5 py-0.5 rounded bg-slate-200/50 font-mono text-xs">
                  {isRTL ? 'RTL' : 'LTR'}
                </code>
              </span>
              <button
                onClick={() => {
                  document.documentElement.dir = isRTL ? 'ltr' : 'rtl';
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/50 border border-white/30 hover:bg-white/70 transition-colors cursor-pointer"
              >
                {isRTL ? (
                  <ToggleRight className="w-4 h-4" />
                ) : (
                  <ToggleLeft className="w-4 h-4" />
                )}
                Toggle to {isRTL ? 'LTR' : 'RTL'}
              </button>
            </div>
          </PreviewBox>
        </GlassCard>
      </motion.div>

      {/* ----------------------------------------------------------------- */}
      {/* useInfiniteScroll                                                 */}
      {/* ----------------------------------------------------------------- */}
      <motion.div variants={staggerItem}>
        <GlassCard title="useInfiniteScroll">
          <PreviewBox>
            <div className="flex items-start gap-3">
              <Code2 className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-700">
                  Hook signature:
                </p>
                <code className="block text-xs font-mono bg-slate-200/50 p-2 rounded-lg whitespace-pre-wrap">
                  {`useInfiniteScroll({
  fetchNextPage: () => void,
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  threshold?: number,
  rootMargin?: string,
}): RefObject<HTMLDivElement | null>`}
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  Returns a ref to attach to a sentinel element. Calls
                  fetchNextPage when the sentinel enters the viewport.
                </p>
              </div>
            </div>
          </PreviewBox>
        </GlassCard>
      </motion.div>

      {/* ----------------------------------------------------------------- */}
      {/* useSelectionSet                                                   */}
      {/* ----------------------------------------------------------------- */}
      <motion.div variants={staggerItem}>
        <GlassCard title="useSelectionSet">
          <PreviewBox>
            <div className="space-y-3">
              {/* Toolbar */}
              <div className="flex items-center gap-3">
                <button
                  onClick={selection.selectAll}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white/50 border border-white/30 hover:bg-white/70 transition-colors cursor-pointer"
                >
                  {t('designSystem.selectAll')}
                </button>
                <button
                  onClick={selection.clear}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white/50 border border-white/30 hover:bg-white/70 transition-colors cursor-pointer"
                >
                  {t('designSystem.clearSelection')}
                </button>
                <span className="text-xs font-medium text-slate-500">
                  {selection.count} / {SELECTION_ITEMS.length} selected
                </span>
              </div>

              {/* Item list */}
              <div className="space-y-1.5">
                {SELECTION_ITEMS.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-white/40 transition-colors"
                    style={{
                      backgroundColor: selection.isSelected(item.id)
                        ? 'rgba(59, 130, 246, 0.08)'
                        : undefined,
                    }}
                  >
                    {selection.isSelected(item.id) ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={selection.isSelected(item.id)}
                      onChange={() => selection.toggle(item.id)}
                    />
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="text-xs text-slate-400">id: {item.id}</span>
                  </label>
                ))}
              </div>
            </div>
          </PreviewBox>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}
